import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../../lib/apiClient";
import "./admin.css";
import {
  generateBookingSheetPdf,
  generateBookingSheetsPdf,
} from "../../utils/generateBookingSheetPdf";
import RoomPlanPicker from "../../booking/pages/RoomPickerModal";
import amocEmailBadge from "../../assets/admin-email-badges/amoc-badge.png";
import jampotEmailBadge from "../../assets/admin-email-badges/jampot-rally-badge.png";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const ACCOMMODATION_COLOURS = ["#3b82f6", "#22c55e", "#f59e0b", "#94a3b8"];
const SHIRT_SIZES = ["S", "M", "L", "XL", "XXL"];
const PRINT_REPORTS = [
  {
    key: "meals",
    label: "Meals booked",
    description: "Individual paid bookings with meal package and BBQ quantities.",
  },
  {
    key: "shirts",
    label: "Shirts ordered",
    description: "Paid bookings with polo shirt quantities by size.",
  },
  {
    key: "rooms",
    label: "Boarding rooms",
    description: "Mount Kelly room list with room size and booking party names.",
  },
  {
    key: "checkin-form",
    label: "Check-in form",
    description: "Alphabetical sign-in sheet with bike, registration and ridden-mileage fields.",
  },
  {
    key: "attendee-list",
    label: "Everyone staying",
    description: "Alphabetical list of every named attendee, with inherited mobile and accommodation.",
  },
];
const GROUP_EMAIL_CATEGORIES = [
  {
    key: "everyone",
    label: "Everyone",
    description: "All paid bookings with an email address.",
    matches: () => true,
  },
  {
    key: "shirts",
    label: "Rally shirts",
    description: "Paid bookings with one or more rally shirts ordered.",
    matches: (booking) => sumShirtsOrdered(booking.merch) > 0,
  },
  {
    key: "camp",
    label: "Tent campers",
    description: "Paid bookings staying on tent pitches.",
    matches: (booking) => normaliseAccommodation(booking.accommodation) === "camp",
  },
  {
    key: "camper",
    label: "Campervans",
    description: "Paid bookings staying in campervans.",
    matches: (booking) => normaliseAccommodation(booking.accommodation) === "camper",
  },
  {
    key: "room",
    label: "Boarding accommodation",
    description: "Paid bookings with Mount Kelly rooms.",
    matches: (booking) => normaliseAccommodation(booking.accommodation) === "room",
  },
  {
    key: "meals",
    label: "Meals booked",
    description: "Paid bookings with the rally meals package or extra BBQ passes.",
    matches: (booking) => getMealCovers(booking) > 0 || getBbqPasses(booking) > 0,
  },
];

function formatCurrency(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

function normaliseAccommodation(value) {
  if (!value) return "none";
  return String(value).toLowerCase();
}

function accommodationLabel(value) {
  switch (normaliseAccommodation(value)) {
    case "room":
      return "Room";
    case "camp":
      return "Camp";
    case "camper":
      return "Camper";
    case "none":
    default:
      return "None";
  }
}

function getStatusClass(status) {
  switch ((status || "").toLowerCase()) {
    case "paid":
      return "jp-admin-badge jp-admin-badge--paid";
    case "pending":
      return "jp-admin-badge jp-admin-badge--pending";
    case "cancelled":
      return "jp-admin-badge jp-admin-badge--cancelled";
    case "expired":
      return "jp-admin-badge jp-admin-badge--expired";
    default:
      return "jp-admin-badge";
  }
}

function getAccommodationClass(accommodation) {
  switch (normaliseAccommodation(accommodation)) {
    case "room":
      return "jp-admin-badge jp-admin-badge--room";
    case "camp":
      return "jp-admin-badge jp-admin-badge--camp";
    case "camper":
      return "jp-admin-badge jp-admin-badge--camper";
    case "none":
    default:
      return "jp-admin-badge jp-admin-badge--none";
  }
}

function buildRoomsSummary(roomsBooked) {
  if (!Array.isArray(roomsBooked) || roomsBooked.length === 0) return "—";

  return roomsBooked
    .map((room) => {
      const roomName = room?.roomName || "Unknown room";
      const capacity = room?.capacity ? ` (${room.capacity})` : "";
      return `${roomName}${capacity}`;
    })
    .join(", ");
}

function buildMerchSummary(merch) {
  if (!merch || typeof merch !== "object") return "—";

  const bits = [];
  const badges = Number(merch.badges || 0);

  if (badges > 0) {
    bits.push(`${badges} badge${badges === 1 ? "" : "s"}`);
  }

  const bbqPasses = Number(merch.bbqPasses || 0);
  if (bbqPasses > 0) {
    bits.push(`${bbqPasses} BBQ pass${bbqPasses === 1 ? "" : "es"}`);
  }

  const shirts = merch.shirts || {};
  const shirtBits = SHIRT_SIZES
    .map((size) => {
      const qty = Number(shirts?.[size] || 0);
      return qty > 0 ? `${qty}×${size}` : null;
    })
    .filter(Boolean);

  if (shirtBits.length > 0) {
    bits.push(`Shirts: ${shirtBits.join(", ")}`);
  }

  return bits.length ? bits.join(" • ") : "—";
}

function getMealCovers(booking) {
  const explicit = Number(booking?.merch?.mealCovers);
  if (Number.isFinite(explicit) && explicit >= 0) return Math.floor(explicit);
  return booking?.meals_package === "all" ? Number(booking?.total_people || 0) : 0;
}

function getBbqPasses(booking) {
  const explicit = Number(booking?.merch?.bbqPasses);
  if (!Number.isFinite(explicit) || explicit < 0) return 0;
  return Math.floor(explicit);
}

function getCoeliacMealCovers(booking) {
  const explicit = Number(booking?.merch?.coeliacMealCovers);
  if (!Number.isFinite(explicit) || explicit < 0) return 0;
  return Math.min(Math.floor(explicit), getMealCovers(booking));
}

function buildMealsSummary(booking) {
  const covers = getMealCovers(booking);
  const bbqPasses = getBbqPasses(booking);
  const coeliacCovers = getCoeliacMealCovers(booking);
  const bits = [];

  if (covers > 0) {
    bits.push(
      `${covers} meal package${covers === 1 ? "" : "s"}${
        coeliacCovers > 0 ? ` (${coeliacCovers} coeliac)` : ""
      }`
    );
  }

  if (bbqPasses > 0) {
    bits.push(`${bbqPasses} extra BBQ pass${bbqPasses === 1 ? "" : "es"}`);
  }

  return bits.length ? bits.join(" • ") : "No meals";
}

function sumShirtsOrdered(merch) {
  const shirts = merch?.shirts || {};

  return SHIRT_SIZES.reduce(
    (sum, size) => sum + Number(shirts?.[size] || 0),
    0
  );
}

function buildAccommodationSummary(booking) {
  const accommodation = normaliseAccommodation(booking.accommodation);

  if (accommodation === "room") {
    return buildRoomsSummary(booking.rooms_booked);
  }

  if (accommodation === "camp") {
    const n = Number(booking.camp_pitches || 0);
    return `${n} camp pitch${n === 1 ? "" : "es"}`;
  }

  if (accommodation === "camper") {
    const n = Number(booking.camper_pitches || 0);
    return `${n} camper pitch${n === 1 ? "" : "es"}`;
  }

  return "No accommodation";
}

function buildAccommodationTypeSummary(booking) {
  switch (normaliseAccommodation(booking.accommodation)) {
    case "room":
      return "Room";
    case "camp":
      return "Camp";
    case "camper":
      return "Camper";
    default:
      return "None";
  }
}

function buildRoomNameSummary(booking) {
  return normaliseAccommodation(booking.accommodation) === "room"
    ? buildRoomsSummary(booking.rooms_booked)
    : "—";
}

function attendeeNames(attendees, type) {
  if (!Array.isArray(attendees)) return [];

  return attendees
    .filter((a) => a?.type === type)
    .map((a) => a?.name)
    .filter(Boolean);
}

function buildFullName(booking) {
  return `${booking.first_name || ""} ${booking.last_name || ""}`.trim() || "—";
}

function compareBookingsAlphabetically(a, b) {
  const aLast = String(a?.last_name || "").trim();
  const bLast = String(b?.last_name || "").trim();
  const aFirst = String(a?.first_name || "").trim();
  const bFirst = String(b?.first_name || "").trim();
  const aKey = `${aLast} ${aFirst}`.trim() || buildFullName(a);
  const bKey = `${bLast} ${bFirst}`.trim() || buildFullName(b);

  return aKey.localeCompare(bKey, "en-GB", { sensitivity: "base" });
}

function bookingCompanionNames(booking) {
  const leadName = buildFullName(booking).trim().toLowerCase();

  return [
    ...attendeeNames(booking.attendees, "adult"),
    ...attendeeNames(booking.attendees, "child"),
  ].filter((name) => String(name).trim().toLowerCase() !== leadName);
}

function bookingPartyNames(booking) {
  const names = [buildFullName(booking), ...bookingCompanionNames(booking)]
    .map((name) => String(name || "").trim())
    .filter((name) => name && name !== "—");

  return names.length ? names.join(", ") : "—";
}

function attendeeNameSortKey(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length <= 1) return parts[0] || "";

  return `${parts[parts.length - 1]} ${parts.slice(0, -1).join(" ")}`;
}

function compareAttendeeRowsAlphabetically(a, b) {
  return attendeeNameSortKey(a.name).localeCompare(attendeeNameSortKey(b.name), "en-GB", {
    sensitivity: "base",
  });
}

function buildAttendeeListRowsForBooking(booking) {
  const phone = booking.phone || "—";
  const accommodation = buildAccommodationSummary(booking);
  const leadName = buildFullName(booking);
  const leadKey = leadName.trim().toLowerCase();
  const companionNames = [
    ...attendeeNames(booking.attendees, "adult"),
    ...attendeeNames(booking.attendees, "child"),
  ].filter((name) => String(name).trim().toLowerCase() !== leadKey);

  return [leadName, ...companionNames]
    .map((name) => String(name || "").trim())
    .filter((name) => name && name !== "—")
    .map((name) => ({
      name,
      phone,
      accommodation,
      bookingId: booking.id,
    }));
}

function buildShirtSizeSummary(merch) {
  const shirts = merch?.shirts || {};
  const bits = SHIRT_SIZES.map((size) => {
    const qty = Number(shirts?.[size] || 0);
    return qty > 0 ? `${size}: ${qty}` : null;
  }).filter(Boolean);

  return bits.length ? bits.join(", ") : "—";
}

function countExtraBadges(booking) {
  return Number(booking?.merch?.badges || 0);
}

function roomCapacityLabel(room) {
  const capacity = Number(room?.capacity || 0);
  return capacity > 0 ? `${capacity} bed${capacity === 1 ? "" : "s"}` : "—";
}

function normaliseRoomSelection(room, roomsById = {}) {
  const roomId = typeof room === "string" ? room : room?.roomName || room?.name;
  const roomInfo = roomsById?.[roomId] || {};
  const roomName = String(roomInfo.name || roomId || "").trim();
  if (!roomName) return null;

  return {
    roomName,
    capacity: Number(roomInfo.capacity || room?.capacity || 0),
    ...(roomInfo.floorKey || room?.floorKey
      ? { floorKey: roomInfo.floorKey || room.floorKey }
      : {}),
  };
}

function roomIdsFromBooking(booking) {
  return (Array.isArray(booking?.rooms_booked) ? booking.rooms_booked : [])
    .map((room) => room?.roomName || room?.name)
    .filter(Boolean);
}

function roomsByIdFromBooking(booking) {
  return (Array.isArray(booking?.rooms_booked) ? booking.rooms_booked : []).reduce(
    (rooms, room) => {
      const roomName = room?.roomName || room?.name;
      if (!roomName) return rooms;

      rooms[roomName] = {
        name: roomName,
        capacity: Number(room?.capacity || 0),
        ...(room?.floorKey ? { floorKey: room.floorKey } : {}),
      };
      return rooms;
    },
    {}
  );
}

function bookingBlocksRooms(booking) {
  return ["paid", "pending"].includes(String(booking?.status || "").toLowerCase());
}

function isPaidBooking(booking) {
  return String(booking.status || "").toLowerCase() === "paid";
}

function dedupeBookingsByEmail(bookingsForEmail) {
  const seen = new Set();

  return bookingsForEmail.filter((booking) => {
    const email = String(booking.email || "").trim().toLowerCase();
    if (!email || seen.has(email)) return false;

    seen.add(email);
    return true;
  });
}

function buildGroupEmailBody(message) {
  const cleanMessage = message.trim();

  return [
    "Jampot Rally 2026",
    "Devon & Cornwall Section",
    "",
    cleanMessage || "Add your message here.",
    "",
    "Kind regards,",
    "Jampot Rally 2026 Team",
    "AJS & Matchless Owners Club - Devon & Cornwall Section",
  ].join("\n");
}

function buildStyledPasteInstructionBody() {
  return [
    "The styled Jampot Rally email has been copied to your clipboard.",
    "",
    "Click in this message body, press Command+V, then delete these instruction lines before sending.",
    "",
    "If the styled version does not paste correctly, use the Open Plain Text Draft button in the admin page instead.",
  ].join("\n");
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildBadgeMarkup(src) {
  const safeSrc = escapeHtml(src || "");

  return safeSrc
    ? `<img src="${safeSrc}" width="54" height="54" alt="" style="display:block;width:54px;height:54px;border:0;border-radius:50%;">`
    : "";
}

function buildGroupEmailHtml(subject, draftBody, leftBadgeSrc, rightBadgeSrc) {
  const safeSubject = escapeHtml(subject || "Jampot Rally 2026 update");
  const safeDraftBody = escapeHtml(draftBody.trim() || "Add your message here.").replace(/\n/g, "<br>");
  const leftBadgeMarkup = buildBadgeMarkup(leftBadgeSrc);
  const rightBadgeMarkup = buildBadgeMarkup(rightBadgeSrc);

  return `
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" bgcolor="#f6f2e8" style="margin:0;padding:0;background-color:#f6f2e8;border-collapse:collapse;color:#253245;font-family:Arial,Helvetica,sans-serif;">
      <tr>
        <td align="center" bgcolor="#f6f2e8" style="padding:18px;background-color:#f6f2e8;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" bgcolor="#f6f2e8" style="max-width:880px;background-color:#f6f2e8;border-collapse:collapse;color:#253245;">
            <tr>
              <td>
                <table role="presentation" width="100%" cellspacing="0" cellpadding="0" bgcolor="#172034" style="background-color:#172034;color:#f8f1dd;border-collapse:collapse;">
                  <tr>
                    <td width="84" bgcolor="#172034" style="padding:20px 0 18px 28px;vertical-align:top;background-color:#172034;">${leftBadgeMarkup}</td>
                    <td bgcolor="#172034" style="padding:22px 12px 18px;text-align:center;vertical-align:top;background-color:#172034;">
                      <div style="font-size:14px;line-height:1.2;letter-spacing:3px;text-transform:uppercase;color:#fffaf0;font-weight:800;">Jampot Rally 2026</div>
                      <div style="font-size:14px;line-height:1.2;letter-spacing:3px;text-transform:uppercase;color:#d6b871;font-weight:800;margin-top:10px;">Devon &amp; Cornwall</div>
                    </td>
                    <td width="84" align="right" bgcolor="#172034" style="padding:20px 28px 18px 0;vertical-align:top;background-color:#172034;">${rightBadgeMarkup}</td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td bgcolor="#f6f2e8" style="padding:0 24px 24px;background-color:#f6f2e8;">
                <table role="presentation" width="100%" cellspacing="0" cellpadding="0" bgcolor="#fff7e6" style="background-color:#fff7e6;border-collapse:collapse;color:#273140;">
                  <tr>
                    <td bgcolor="#fff7e6" style="background-color:#fff7e6;color:#273140;padding:28px 32px 30px;">
                      <div style="font-size:14px;letter-spacing:3px;text-transform:uppercase;color:#8a6500;font-weight:800;margin-bottom:16px;">AJS &amp; Matchless Owners Club</div>
                      <h1 style="margin:0 0 24px;font-family:Georgia,'Times New Roman',serif;font-size:30px;font-weight:400;line-height:1.2;color:#253245;">${safeSubject}</h1>
                      <div style="font-size:18px;line-height:1.75;color:#273140;">${safeDraftBody}</div>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `.trim();
}

function copyHtmlWithEventFallback(html, plainText) {
  function handleCopy(event) {
    event.preventDefault();
    event.clipboardData.setData("text/html", html);
    event.clipboardData.setData("text/plain", plainText);
  }

  document.addEventListener("copy", handleCopy);

  try {
    return document.execCommand("copy");
  } finally {
    document.removeEventListener("copy", handleCopy);
  }
}

function copyHtmlWithSelectionFallback(html) {
  const node = document.createElement("div");
  node.setAttribute("contenteditable", "true");
  node.style.left = "-9999px";
  node.style.position = "fixed";
  node.style.top = "0";
  node.innerHTML = html;
  document.body.appendChild(node);

  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(node);
  selection.removeAllRanges();
  selection.addRange(range);

  try {
    return document.execCommand("copy");
  } finally {
    selection.removeAllRanges();
    node.remove();
  }
}

export default function AdminBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("paid");
  const [accommodationFilter, setAccommodationFilter] = useState("all");
  const [activeAdminTab, setActiveAdminTab] = useState("dashboard");
  const [checkInSearch, setCheckInSearch] = useState("");
  const [checkInStatusFilter, setCheckInStatusFilter] = useState("all");
  const [checkedInBookings, setCheckedInBookings] = useState({});
  const [printReport, setPrintReport] = useState("meals");

  const [resendingId, setResendingId] = useState(null);
  const [pdfBookingId, setPdfBookingId] = useState(null);
  const [pricedPdfBookingId, setPricedPdfBookingId] = useState(null);
  const [paidPdfGenerating, setPaidPdfGenerating] = useState(false);
  const [paidPricedPdfGenerating, setPaidPricedPdfGenerating] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const [activeMenuBookingId, setActiveMenuBookingId] = useState(null);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [bookingDetailMode, setBookingDetailMode] = useState("admin");
  const [coeliacMealDraft, setCoeliacMealDraft] = useState(0);
  const [coeliacMealSaving, setCoeliacMealSaving] = useState(false);
  const [coeliacMealMessage, setCoeliacMealMessage] = useState("");
  const [groupEmailCategory, setGroupEmailCategory] = useState("everyone");
  const [groupEmailSubject, setGroupEmailSubject] = useState("Jampot Rally 2026 update");
  const [groupEmailMessage, setGroupEmailMessage] = useState(
    "Hello,\n\nHere is an update for your Jampot Rally 2026 booking.\n\n"
  );
  const [groupEmailNotice, setGroupEmailNotice] = useState("");
  const [editBookingSearch, setEditBookingSearch] = useState("");
  const [editBookingId, setEditBookingId] = useState("");
  const [editAccommodationDraft, setEditAccommodationDraft] = useState({
    accommodation: "none",
    campPitches: 0,
    camperPitches: 0,
    selectedRoomIds: [],
  });
  const [editRoomsById, setEditRoomsById] = useState({});
  const [editRoomPickerOpen, setEditRoomPickerOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState("");

  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const [loginSubmitting, setLoginSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      setAuthLoading(true);
      setAuthError("");

      try {
        const res = await fetch("/.netlify/functions/admin-session", {
          credentials: "include",
        });

        if (!cancelled) {
          setIsAuthed(res.ok);
        }
      } catch {
        if (!cancelled) {
          setIsAuthed(false);
        }
      } finally {
        if (!cancelled) {
          setAuthChecked(true);
          setAuthLoading(false);
        }
      }
    }

    checkSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadBookings() {
      if (!isAuthed) {
        setBookings([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError("");

      try {
        const data = await apiGet("/.netlify/functions/admin-bookings", {
          credentials: "include",
        });

        if (cancelled) return;

        setBookings(data.bookings || []);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.message || "Failed to load bookings");
          setBookings([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadBookings();

    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  useEffect(() => {
    function handleDocumentClick() {
      setActiveMenuBookingId(null);
    }

    if (activeMenuBookingId !== null) {
      document.addEventListener("click", handleDocumentClick);
    }

    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [activeMenuBookingId]);

  const filteredBookings = useMemo(() => {
    const q = search.trim().toLowerCase();

    return bookings.filter((booking) => {
      const fullName = buildFullName(booking).toLowerCase();
      const email = String(booking.email || "").toLowerCase();
      const phone = String(booking.phone || "").toLowerCase();
      const status = String(booking.status || "").toLowerCase();
      const accommodation = normaliseAccommodation(booking.accommodation);
      const rooms = buildRoomsSummary(booking.rooms_booked).toLowerCase();
      const merch = buildMerchSummary(booking.merch).toLowerCase();

      const matchesSearch =
        !q ||
        fullName.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        rooms.includes(q) ||
        merch.includes(q);

      const matchesStatus =
        statusFilter === "all" || status === statusFilter;

      const matchesAccommodation =
        accommodationFilter === "all" ||
        accommodation === accommodationFilter;

      return matchesSearch && matchesStatus && matchesAccommodation;
    });
  }, [bookings, search, statusFilter, accommodationFilter]);

  const paidVisibleBookings = useMemo(() => {
    const q = search.trim().toLowerCase();

    return bookings.filter((booking) => {
      const fullName = buildFullName(booking).toLowerCase();
      const email = String(booking.email || "").toLowerCase();
      const phone = String(booking.phone || "").toLowerCase();
      const accommodation = normaliseAccommodation(booking.accommodation);
      const rooms = buildRoomsSummary(booking.rooms_booked).toLowerCase();
      const merch = buildMerchSummary(booking.merch).toLowerCase();
      const isPaid = String(booking.status || "").toLowerCase() === "paid";

      const matchesSearch =
        !q ||
        fullName.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        rooms.includes(q) ||
        merch.includes(q);

      const matchesAccommodation =
        accommodationFilter === "all" ||
        accommodation === accommodationFilter;

      return matchesSearch && matchesAccommodation && isPaid;
    });
  }, [bookings, search, accommodationFilter]);

  const paidIncomeTotal = useMemo(() => {
    return paidVisibleBookings.reduce(
      (sum, booking) => sum + Number(booking.total_cost || 0),
      0
    );
  }, [paidVisibleBookings]);

  const paidPeopleTotal = useMemo(() => {
    return paidVisibleBookings.reduce(
      (sum, booking) => sum + Number(booking.total_people || 0),
      0
    );
  }, [paidVisibleBookings]);

  const averagePaidBookingValue = useMemo(() => {
    if (!paidVisibleBookings.length) return 0;
    return paidIncomeTotal / paidVisibleBookings.length;
  }, [paidIncomeTotal, paidVisibleBookings]);

  const paidBookings = useMemo(() => {
    return bookings
      .filter((booking) => isPaidBooking(booking))
      .sort(compareBookingsAlphabetically);
  }, [bookings]);

  const editBookingOptions = useMemo(() => {
    const q = editBookingSearch.trim().toLowerCase();

    return bookings
      .filter((booking) => {
        if (!q) return true;

        return [
          booking.id,
          buildFullName(booking),
          booking.email,
          booking.phone,
          accommodationLabel(booking.accommodation),
          buildAccommodationSummary(booking),
        ]
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => buildFullName(a).localeCompare(buildFullName(b)))
      .slice(0, 80);
  }, [bookings, editBookingSearch]);

  const selectedEditBooking = useMemo(() => {
    return bookings.find((booking) => String(booking.id) === String(editBookingId)) || null;
  }, [bookings, editBookingId]);

  useEffect(() => {
    if (!selectedEditBooking) {
      setEditAccommodationDraft({
        accommodation: "none",
        campPitches: 0,
        camperPitches: 0,
        selectedRoomIds: [],
      });
      setEditRoomsById({});
      setEditMessage("");
      return;
    }

    setEditAccommodationDraft({
      accommodation: normaliseAccommodation(selectedEditBooking.accommodation),
      campPitches: Number(selectedEditBooking.camp_pitches || 0),
      camperPitches: Number(selectedEditBooking.camper_pitches || 0),
      selectedRoomIds: roomIdsFromBooking(selectedEditBooking),
    });
    setEditRoomsById(roomsByIdFromBooking(selectedEditBooking));
    setEditMessage("");
  }, [selectedEditBooking]);

  const unavailableRoomIdsForEdit = useMemo(() => {
    if (!selectedEditBooking) return [];

    const selectedBookingId = String(selectedEditBooking.id);
    return bookings
      .filter((booking) => String(booking.id) !== selectedBookingId && bookingBlocksRooms(booking))
      .flatMap((booking) => roomIdsFromBooking(booking));
  }, [bookings, selectedEditBooking]);

  const selectedEditRooms = useMemo(() => {
    return editAccommodationDraft.selectedRoomIds
      .map((roomId) => normaliseRoomSelection(roomId, editRoomsById))
      .filter(Boolean);
  }, [editAccommodationDraft.selectedRoomIds, editRoomsById]);

  const editSelectedRoomBeds = useMemo(() => {
    return selectedEditRooms.reduce((sum, room) => sum + Number(room.capacity || 0), 0);
  }, [selectedEditRooms]);

  const checkInBookings = useMemo(() => {
    const q = checkInSearch.trim().toLowerCase();

    return paidBookings.filter((booking) => {
      const isCheckedIn = Boolean(checkedInBookings[booking.id]);
      const matchesStatus =
        checkInStatusFilter === "all" ||
        (checkInStatusFilter === "checked-in" && isCheckedIn) ||
        (checkInStatusFilter === "awaiting" && !isCheckedIn);

      if (!matchesStatus) return false;

      const haystack = [
        booking.id,
        buildFullName(booking),
        ...bookingCompanionNames(booking),
        booking.email,
        booking.phone,
        accommodationLabel(booking.accommodation),
        buildAccommodationSummary(booking),
      ]
        .join(" ")
        .toLowerCase();

      return !q || haystack.includes(q);
    });
  }, [checkInSearch, checkInStatusFilter, checkedInBookings, paidBookings]);

  const checkedInCount = useMemo(() => {
    return paidBookings.reduce(
      (sum, booking) => sum + (checkedInBookings[booking.id] ? 1 : 0),
      0
    );
  }, [checkedInBookings, paidBookings]);

  const checkedInPeopleTotal = useMemo(() => {
    return paidBookings.reduce(
      (sum, booking) =>
        sum + (checkedInBookings[booking.id] ? Number(booking.total_people || 0) : 0),
      0
    );
  }, [checkedInBookings, paidBookings]);

  const earlyArrivalBookings = useMemo(() => {
    return paidBookings.filter((booking) => Boolean(booking.early_arrival));
  }, [paidBookings]);

  const standardArrivalBookings = useMemo(() => {
    return paidBookings.filter((booking) => !booking.early_arrival);
  }, [paidBookings]);

  const earlyArrivalPeopleTotal = useMemo(() => {
    return earlyArrivalBookings.reduce(
      (sum, booking) => sum + Number(booking.total_people || 0),
      0
    );
  }, [earlyArrivalBookings]);

  const standardArrivalPeopleTotal = useMemo(() => {
    return standardArrivalBookings.reduce(
      (sum, booking) => sum + Number(booking.total_people || 0),
      0
    );
  }, [standardArrivalBookings]);

  const mealReportRows = useMemo(() => {
    return paidBookings
      .map((booking) => ({
        booking,
        mealCovers: getMealCovers(booking),
        bbqPasses: getBbqPasses(booking),
        coeliacCovers: getCoeliacMealCovers(booking),
      }))
      .filter((row) => row.mealCovers > 0 || row.bbqPasses > 0);
  }, [paidBookings]);

  const shirtReportRows = useMemo(() => {
    return paidBookings
      .map((booking) => ({
        booking,
        shirts: booking.merch?.shirts || {},
        totalShirts: sumShirtsOrdered(booking.merch),
      }))
      .filter((row) => row.totalShirts > 0);
  }, [paidBookings]);

  const roomReportRows = useMemo(() => {
    return paidBookings
      .flatMap((booking) => {
        const rooms = Array.isArray(booking.rooms_booked) ? booking.rooms_booked : [];
        return rooms.map((room, index) => ({
          booking,
          room,
          index,
        }));
      })
      .sort((a, b) => {
        const roomCompare = String(a.room?.roomName || "").localeCompare(
          String(b.room?.roomName || "")
        );
        if (roomCompare !== 0) return roomCompare;
        return buildFullName(a.booking).localeCompare(buildFullName(b.booking));
      });
  }, [paidBookings]);

  const checkInFormRows = useMemo(() => {
    return [...paidBookings].sort(compareBookingsAlphabetically);
  }, [paidBookings]);

  const attendeeListRows = useMemo(() => {
    return paidBookings
      .flatMap((booking) => buildAttendeeListRowsForBooking(booking))
      .sort(compareAttendeeRowsAlphabetically);
  }, [paidBookings]);

  const reportCounts = useMemo(
    () => ({
      "attendee-list": attendeeListRows.length,
      meals: mealReportRows.length,
      shirts: shirtReportRows.length,
      rooms: roomReportRows.length,
      "checkin-form": checkInFormRows.length,
    }),
    [
      attendeeListRows.length,
      checkInFormRows.length,
      mealReportRows.length,
      roomReportRows.length,
      shirtReportRows.length,
    ]
  );

  const mealReportTotals = useMemo(() => {
    return mealReportRows.reduce(
      (totals, row) => ({
        mealCovers: totals.mealCovers + row.mealCovers,
        bbqPasses: totals.bbqPasses + row.bbqPasses,
        sundayBbqCovers: totals.sundayBbqCovers + row.mealCovers + row.bbqPasses,
        coeliacCovers: totals.coeliacCovers + row.coeliacCovers,
      }),
      { mealCovers: 0, bbqPasses: 0, sundayBbqCovers: 0, coeliacCovers: 0 }
    );
  }, [mealReportRows]);

  const shirtReportTotals = useMemo(() => {
    return shirtReportRows.reduce(
      (totals, row) => {
        for (const size of SHIRT_SIZES) {
          totals.sizes[size] += Number(row.shirts?.[size] || 0);
        }
        totals.total += row.totalShirts;
        return totals;
      },
      {
        total: 0,
        sizes: SHIRT_SIZES.reduce((sizes, size) => ({ ...sizes, [size]: 0 }), {}),
      }
    );
  }, [shirtReportRows]);

  const selectedPrintReport = useMemo(() => {
    return (
      PRINT_REPORTS.find((report) => report.key === printReport) || PRINT_REPORTS[0]
    );
  }, [printReport]);

  const visiblePeopleTotal = useMemo(() => {
    return filteredBookings.reduce(
      (sum, booking) => sum + Number(booking.total_people || 0),
      0
    );
  }, [filteredBookings]);

  const visibleCostTotal = useMemo(() => {
    return filteredBookings.reduce(
      (sum, booking) => sum + Number(booking.total_cost || 0),
      0
    );
  }, [filteredBookings]);

  const accommodationPieData = useMemo(() => {
    const counts = {
      room: 0,
      camp: 0,
      camper: 0,
      none: 0,
    };

    for (const booking of paidVisibleBookings) {
      const key = normaliseAccommodation(booking.accommodation);
      counts[key] = (counts[key] || 0) + 1;
    }

    return [
      { name: "Room", value: counts.room },
      { name: "Camp", value: counts.camp },
      { name: "Camper", value: counts.camper },
      { name: "None", value: counts.none },
    ].filter((item) => item.value > 0);
  }, [paidVisibleBookings]);

  const cumulativeIncomeData = useMemo(() => {
    if (!paidVisibleBookings.length) return [];

    const sorted = [...paidVisibleBookings].sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return aTime - bTime;
    });

    const dailyTotals = new Map();

    for (const booking of sorted) {
      const d = new Date(booking.created_at);
      if (Number.isNaN(d.getTime())) continue;

      const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;

      dailyTotals.set(
        dayKey,
        (dailyTotals.get(dayKey) || 0) + Number(booking.total_cost || 0)
      );
    }

    const firstDate = new Date(sorted[0].created_at);
    const lastDate = new Date(sorted[sorted.length - 1].created_at);

    if (Number.isNaN(firstDate.getTime()) || Number.isNaN(lastDate.getTime())) {
      return [];
    }

    firstDate.setHours(0, 0, 0, 0);
    lastDate.setHours(0, 0, 0, 0);

    const data = [];
    let runningTotal = 0;

    for (const d = new Date(firstDate); d <= lastDate; d.setDate(d.getDate() + 1)) {
      const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;

      const dayIncome = dailyTotals.get(dayKey) || 0;
      runningTotal += dayIncome;

      data.push({
        date: dayKey,
        shortLabel: new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
        }).format(d),
        fullDate: new Intl.DateTimeFormat("en-GB", {
          dateStyle: "medium",
        }).format(d),
        dayIncome,
        cumulativeIncome: runningTotal,
      });
    }

    return data;
  }, [paidVisibleBookings]);

  const groupEmailCategoryCounts = useMemo(() => {
    return GROUP_EMAIL_CATEGORIES.reduce((counts, category) => {
      const categoryBookings = dedupeBookingsByEmail(
        bookings.filter((booking) => isPaidBooking(booking) && category.matches(booking))
      );

      counts[category.key] = {
        bookings: categoryBookings.length,
        people: categoryBookings.reduce(
          (sum, booking) => sum + Number(booking.total_people || 0),
          0
        ),
      };

      return counts;
    }, {});
  }, [bookings]);

  const selectedGroupEmailCategory = useMemo(() => {
    return (
      GROUP_EMAIL_CATEGORIES.find((category) => category.key === groupEmailCategory) ||
      GROUP_EMAIL_CATEGORIES[0]
    );
  }, [groupEmailCategory]);

  const groupEmailRecipients = useMemo(() => {
    return dedupeBookingsByEmail(
      bookings.filter(
        (booking) => isPaidBooking(booking) && selectedGroupEmailCategory.matches(booking)
      )
    ).sort((a, b) => buildFullName(a).localeCompare(buildFullName(b)));
  }, [bookings, selectedGroupEmailCategory]);

  const groupEmailAddresses = useMemo(() => {
    return groupEmailRecipients
      .map((booking) => String(booking.email || "").trim())
      .filter(Boolean);
  }, [groupEmailRecipients]);

  const groupEmailDraftBody = useMemo(
    () => buildGroupEmailBody(groupEmailMessage),
    [groupEmailMessage]
  );

  const groupEmailStyledMailtoLength = useMemo(() => {
    const params = new URLSearchParams({
      bcc: groupEmailAddresses.join(","),
      subject: groupEmailSubject.trim() || "Jampot Rally 2026 update",
    });

    return `mailto:?${params.toString()}`.length;
  }, [groupEmailAddresses, groupEmailSubject]);

  async function handleLogin(e) {
    e.preventDefault();
    setAuthError("");
    setLoginSubmitting(true);

    try {
      const res = await fetch("/.netlify/functions/admin-login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ password }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(json?.error || "Login failed");
      }

      setIsAuthed(true);
      setPassword("");
    } catch (e) {
      setAuthError(e?.message || "Login failed");
    } finally {
      setLoginSubmitting(false);
    }
  }

  async function handleLogout() {
    try {
      await fetch("/.netlify/functions/admin-logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore
    }

    setIsAuthed(false);
    setBookings([]);
    setActiveMenuBookingId(null);
    setViewingBooking(null);
    setResendMessage("");
    setLoadError("");
  }

  async function handleResendEmail(booking) {
    setResendMessage("");
    setResendingId(booking.id);

    try {
      const response = await fetch("/.netlify/functions/resend-booking-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          bookingId: booking.id,
        }),
      });

      const json = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          json?.error || json?.message || "Failed to resend email"
        );
      }

      setResendMessage(`Confirmation email resent to ${booking.email}`);
    } catch (e) {
      setResendMessage(e?.message || "Failed to resend confirmation email");
    } finally {
      setResendingId(null);
    }
  }

  async function handleDownloadBookingSheet(booking) {
    try {
      setResendMessage("");
      setPdfBookingId(booking.id);
      await generateBookingSheetPdf(booking);
    } catch (e) {
      setResendMessage(e?.message || "Failed to generate booking sheet PDF");
    } finally {
      setPdfBookingId(null);
    }
  }

  async function handleDownloadPricedBookingSheet(booking) {
    try {
      setResendMessage("");
      setPricedPdfBookingId(booking.id);
      await generateBookingSheetPdf(booking, { includePriceBreakdown: true });
    } catch (e) {
      setResendMessage(e?.message || "Failed to generate priced booking sheet PDF");
    } finally {
      setPricedPdfBookingId(null);
    }
  }

  async function handleDownloadPaidBookingSheets() {
    if (!paidBookings.length) {
      setResendMessage("There are no paid bookings to include in the PDF.");
      return;
    }

    try {
      setResendMessage("");
      setPaidPdfGenerating(true);
      await generateBookingSheetsPdf(paidBookings, "paid-booking-sheets.pdf");
    } catch (e) {
      setResendMessage(e?.message || "Failed to generate paid booking sheets PDF");
    } finally {
      setPaidPdfGenerating(false);
    }
  }

  async function handleDownloadPaidPricedBookingSheets() {
    if (!paidBookings.length) {
      setResendMessage("There are no paid bookings to include in the PDF.");
      return;
    }

    try {
      setResendMessage("");
      setPaidPricedPdfGenerating(true);
      await generateBookingSheetsPdf(
        paidBookings,
        "paid-priced-booking-sheets.pdf",
        { includePriceBreakdown: true }
      );
    } catch (e) {
      setResendMessage(e?.message || "Failed to generate priced paid booking sheets PDF");
    } finally {
      setPaidPricedPdfGenerating(false);
    }
  }

  function openBookingDetail(booking, mode = "admin") {
    setViewingBooking(booking);
    setBookingDetailMode(mode);
    setCoeliacMealDraft(getCoeliacMealCovers(booking));
    setCoeliacMealMessage("");
  }

  function closeBookingDetail() {
    setViewingBooking(null);
    setBookingDetailMode("admin");
    setCoeliacMealDraft(0);
    setCoeliacMealMessage("");
  }

  function updateBookingEverywhere(updatedBooking) {
    setBookings((prev) =>
      prev.map((booking) =>
        booking.id === updatedBooking.id ? { ...booking, ...updatedBooking } : booking
      )
    );
    setViewingBooking((current) =>
      current?.id === updatedBooking.id ? { ...current, ...updatedBooking } : current
    );
  }

  async function handleSaveCoeliacMeals() {
    if (!viewingBooking) return;

    const mealCovers = getMealCovers(viewingBooking);
    const coeliacCovers = Math.max(0, Math.floor(Number(coeliacMealDraft || 0)));

    if (coeliacCovers > mealCovers) {
      setCoeliacMealMessage(`Coeliac meals cannot exceed ${mealCovers}.`);
      return;
    }

    setCoeliacMealSaving(true);
    setCoeliacMealMessage("");

    try {
      const response = await apiPost(
        "/.netlify/functions/admin-booking-meals",
        {
          bookingId: viewingBooking.id,
          coeliacMealCovers: coeliacCovers,
        },
        { credentials: "include" }
      );

      updateBookingEverywhere(response.data);
      setCoeliacMealDraft(getCoeliacMealCovers(response.data));
      setCoeliacMealMessage(
        `Saved to Supabase: merch.coeliacMealCovers = ${response.saved?.coeliacMealCovers ?? coeliacCovers}.`
      );
    } catch (e) {
      setCoeliacMealMessage(e?.message || "Failed to save coeliac meal quantity.");
    } finally {
      setCoeliacMealSaving(false);
    }
  }

  function updateEditAccommodationDraft(patch) {
    setEditAccommodationDraft((prev) => ({
      ...prev,
      ...patch,
    }));
  }

  function handleEditAccommodationChange(value) {
    setEditAccommodationDraft((prev) => ({
      ...prev,
      accommodation: value,
      campPitches: value === "camp" ? Math.max(1, Number(prev.campPitches || 1)) : 0,
      camperPitches: value === "camper" ? Math.max(1, Number(prev.camperPitches || 1)) : 0,
      selectedRoomIds: value === "room" ? prev.selectedRoomIds : [],
    }));
    setEditMessage("");
  }

  async function handleSaveAccommodationEdit() {
    if (!selectedEditBooking) return;

    const accommodation = normaliseAccommodation(editAccommodationDraft.accommodation);

    if (accommodation === "room" && selectedEditRooms.length === 0) {
      setEditMessage("Select at least one room before saving.");
      return;
    }

    setEditSaving(true);
    setEditMessage("");

    try {
      const response = await apiPost(
        "/.netlify/functions/admin-booking-accommodation",
        {
          bookingId: selectedEditBooking.id,
          accommodation,
          campPitches: editAccommodationDraft.campPitches,
          camperPitches: editAccommodationDraft.camperPitches,
          rooms: selectedEditRooms,
        },
        { credentials: "include" }
      );

      updateBookingEverywhere(response.data);
      setEditMessage(`Saved accommodation for ${buildFullName(response.data)}.`);
    } catch (e) {
      setEditMessage(e?.message || "Failed to save accommodation changes.");
    } finally {
      setEditSaving(false);
    }
  }

  function handleToggleCheckIn(bookingId) {
    setCheckedInBookings((prev) => ({
      ...prev,
      [bookingId]: !prev[bookingId],
    }));
  }

  function handlePrintReport() {
    window.print();
  }

  function handleOpenGroupEmailDraft() {
    setGroupEmailNotice("");

    if (groupEmailAddresses.length === 0) {
      setGroupEmailNotice("No paid bookings match this email filter.");
      return;
    }

    const params = new URLSearchParams({
      bcc: groupEmailAddresses.join(","),
      subject: groupEmailSubject.trim() || "Jampot Rally 2026 update",
      body: groupEmailDraftBody,
    });

    window.location.href = `mailto:?${params.toString()}`;
  }

  function openGroupEmailAddressedDraft(body = "") {
    const params = new URLSearchParams({
      bcc: groupEmailAddresses.join(","),
      subject: groupEmailSubject.trim() || "Jampot Rally 2026 update",
    });

    if (body) {
      params.set("body", body);
    }

    window.location.href = `mailto:?${params.toString()}`;
  }

  async function copyStyledGroupEmailToClipboard() {
    const groupEmailDraftHtml = buildGroupEmailHtml(
      groupEmailSubject,
      groupEmailDraftBody,
      amocEmailBadge,
      jampotEmailBadge
    );

    if (window.ClipboardItem && navigator.clipboard?.write) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([groupEmailDraftHtml], { type: "text/html" }),
            "text/plain": new Blob([groupEmailDraftBody], { type: "text/plain" }),
          }),
        ]);
        return true;
      } catch {
        if (copyHtmlWithEventFallback(groupEmailDraftHtml, groupEmailDraftBody)) {
          return true;
        }

        if (copyHtmlWithSelectionFallback(groupEmailDraftHtml)) {
          return true;
        }
      }
    }

    if (copyHtmlWithEventFallback(groupEmailDraftHtml, groupEmailDraftBody)) {
      return true;
    }

    if (copyHtmlWithSelectionFallback(groupEmailDraftHtml)) {
      return true;
    }

    await navigator.clipboard.writeText(groupEmailDraftBody);
    return false;
  }

  async function handleCopyGroupEmailAddresses() {
    setGroupEmailNotice("");

    try {
      await navigator.clipboard.writeText(groupEmailAddresses.join(", "));
      setGroupEmailNotice(`Copied ${groupEmailAddresses.length} email address${groupEmailAddresses.length === 1 ? "" : "es"}.`);
    } catch {
      setGroupEmailNotice("Could not copy email addresses from this browser.");
    }
  }

  async function handleCopyGroupEmailBody() {
    setGroupEmailNotice("");

    try {
      await navigator.clipboard.writeText(groupEmailDraftBody);
      setGroupEmailNotice("Copied the email body.");
    } catch {
      setGroupEmailNotice("Could not copy the email body from this browser.");
    }
  }

  async function handleCopyBookingId(bookingId) {
    const value = String(bookingId || "").trim();
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setResendMessage(`Copied booking ID ${value}.`);
    } catch {
      setResendMessage(`Could not copy booking ID ${value} from this browser.`);
    }
  }

  async function handleCopyStyledGroupEmail() {
    setGroupEmailNotice("");

    try {
      const copiedHtml = await copyStyledGroupEmailToClipboard();

      setGroupEmailNotice(
        copiedHtml
          ? "Copied the styled email. Paste it into the mail draft body."
          : "Copied the plain text email. This browser cannot copy styled HTML."
      );
    } catch {
      setGroupEmailNotice("Could not copy the styled email from this browser.");
    }
  }

  async function handleCopyStyledAndOpenGroupEmailDraft() {
    setGroupEmailNotice("");

    if (groupEmailAddresses.length === 0) {
      setGroupEmailNotice("No paid bookings match this email filter.");
      return;
    }

    try {
      const copiedHtml = await copyStyledGroupEmailToClipboard();
      setGroupEmailNotice(
        copiedHtml
          ? "Styled email copied. When Mail opens, click in the body and press Command+V."
          : "Plain text email copied. When Mail opens, click in the body and press Command+V."
      );
      openGroupEmailAddressedDraft(buildStyledPasteInstructionBody());
    } catch {
      setGroupEmailNotice("Could not copy the email. Try Copy Addresses or Open Plain Text Draft.");
    }
  }

  const renderGroupEmailSection = () => (
    <section className="jp-admin-group-email">
      <div className="jp-admin-group-email__intro">
        <div>
          <div className="jp-admin-kicker">Group Email</div>
          <h2>Send a Jampot Rally update</h2>
          <p>
            Choose a recipient filter, write the message, then open it as a
            draft in your mail app. Recipients are added as BCC.
          </p>
        </div>

        <div className="jp-admin-group-email__count">
          <strong>{groupEmailAddresses.length}</strong>
          <span>recipient{groupEmailAddresses.length === 1 ? "" : "s"}</span>
        </div>
      </div>

      <div className="jp-admin-group-email__categories" role="tablist" aria-label="Email recipient filters">
        {GROUP_EMAIL_CATEGORIES.map((category) => (
          <button
            key={category.key}
            type="button"
            className={
              "jp-admin-group-email__category" +
              (groupEmailCategory === category.key ? " active" : "")
            }
            onClick={() => setGroupEmailCategory(category.key)}
          >
            <span>{category.label}</span>
            <span className="jp-admin-group-email__category-metrics">
              <strong>{groupEmailCategoryCounts[category.key]?.bookings || 0}</strong>
              <small>
                People: {groupEmailCategoryCounts[category.key]?.people || 0}
              </small>
            </span>
          </button>
        ))}
      </div>

      <div className="jp-admin-group-email__body">
        <div className="jp-admin-group-email__form">
          <label className="jp-admin-field-label" htmlFor="jp-group-email-subject">
            Subject
          </label>
          <input
            id="jp-group-email-subject"
            type="text"
            className="jp-admin-input"
            value={groupEmailSubject}
            onChange={(e) => setGroupEmailSubject(e.target.value)}
          />

          <label className="jp-admin-field-label" htmlFor="jp-group-email-content">
            Message content
          </label>
          <textarea
            id="jp-group-email-content"
            className="jp-admin-textarea"
            value={groupEmailMessage}
            rows={9}
            onChange={(e) => setGroupEmailMessage(e.target.value)}
          />

          <div className="jp-admin-group-email__meta">
            <div>
              <strong>{selectedGroupEmailCategory.label}</strong>
              <span>{selectedGroupEmailCategory.description}</span>
            </div>
            <div>
              Styled mail link: {groupEmailStyledMailtoLength.toLocaleString()} chars
            </div>
          </div>

          <div className="jp-admin-group-email__warning">
            Mail app links cannot pre-fill a styled HTML body. This button copies the
            styled email first, opens Mail with the recipients and subject, then you
            paste it into the body with Command+V.
          </div>

          {groupEmailNotice ? (
            <div className="jp-admin-group-email__notice">{groupEmailNotice}</div>
          ) : null}

          <div className="jp-admin-group-email__actions">
            <button
              type="button"
              className="jp-admin-btn"
              onClick={handleCopyStyledAndOpenGroupEmailDraft}
              disabled={groupEmailAddresses.length === 0}
            >
              Copy Styled Body, Then Open Mail
            </button>
            <button
              type="button"
              className="jp-admin-btn jp-admin-btn--secondary"
              onClick={handleOpenGroupEmailDraft}
              disabled={groupEmailAddresses.length === 0}
            >
              Open Plain Text Draft
            </button>
            <button
              type="button"
              className="jp-admin-btn jp-admin-btn--secondary"
              onClick={handleCopyGroupEmailAddresses}
              disabled={groupEmailAddresses.length === 0}
            >
              Copy Addresses
            </button>
            <button
              type="button"
              className="jp-admin-btn jp-admin-btn--secondary"
              onClick={handleCopyStyledGroupEmail}
            >
              Copy Styled Email
            </button>
            <button
              type="button"
              className="jp-admin-btn jp-admin-btn--secondary"
              onClick={handleCopyGroupEmailBody}
            >
              Copy Body
            </button>
          </div>
        </div>

        <div className="jp-admin-email-preview" aria-label="Email preview">
          <div className="jp-admin-email-preview__masthead">
            <img src={amocEmailBadge} alt="" aria-hidden="true" />
            <div>
              <span>Jampot Rally 2026</span>
              <strong>Devon &amp; Cornwall</strong>
            </div>
            <img src={jampotEmailBadge} alt="" aria-hidden="true" />
          </div>

          <div className="jp-admin-email-preview__paper">
            <p className="jp-admin-email-preview__eyebrow">
              AJS &amp; Matchless Owners Club
            </p>
            <h3>{groupEmailSubject || "Jampot Rally 2026 update"}</h3>
            <pre>{groupEmailDraftBody}</pre>
          </div>

          <div className="jp-admin-email-preview__recipients">
            <strong>BCC recipients</strong>
            <span>
              {groupEmailAddresses.length
                ? groupEmailAddresses.slice(0, 6).join(", ")
                : "No recipients in this filter"}
              {groupEmailAddresses.length > 6 ? `, +${groupEmailAddresses.length - 6} more` : ""}
            </span>
          </div>
        </div>
      </div>
    </section>
  );

  const renderEditBookingsSection = () => (
    <section className="jp-admin-edit-bookings" aria-label="Edit booking accommodation">
      <div className="jp-admin-edit-bookings__intro">
        <div>
          <div className="jp-admin-kicker">Edit Bookings</div>
          <h2>Manual accommodation changes</h2>
          <p>
            Use this when someone phones or emails to change accommodation. Room choices are checked
            against other paid and pending bookings before saving.
          </p>
          <div className="jp-admin-edit-bookings__warning">
            This updates the accommodation record only. Handle any extra payment, refund, or follow-up
            confirmation separately.
          </div>
        </div>
      </div>

      <div className="jp-admin-edit-grid">
        <div className="jp-admin-edit-card">
          <label className="jp-admin-field-label" htmlFor="jp-admin-edit-search">
            Find booking
          </label>
          <input
            id="jp-admin-edit-search"
            type="text"
            className="jp-admin-input"
            placeholder="Search by name, email, phone, room or booking ID..."
            value={editBookingSearch}
            onChange={(e) => setEditBookingSearch(e.target.value)}
          />

          <label className="jp-admin-field-label" htmlFor="jp-admin-edit-booking">
            Booking
          </label>
          <select
            id="jp-admin-edit-booking"
            className="jp-admin-select"
            value={editBookingId}
            onChange={(e) => setEditBookingId(e.target.value)}
          >
            <option value="">Select a booking</option>
            {editBookingOptions.map((booking) => (
              <option key={booking.id} value={booking.id}>
                {buildFullName(booking)} - {booking.email || "no email"} - {booking.id}
              </option>
            ))}
          </select>

          {loading ? (
            <div className="jp-admin-edit-bookings__note">Loading bookings...</div>
          ) : loadError ? (
            <div className="jp-admin-message">Failed to load bookings: {loadError}</div>
          ) : null}

          {selectedEditBooking ? (
            <div className="jp-admin-edit-bookings__summary">
              <div>
                <strong>{buildFullName(selectedEditBooking)}</strong>
                <span>{selectedEditBooking.email || "No email"}</span>
              </div>
              <div>
                <strong>Current accommodation</strong>
                <span>{buildAccommodationSummary(selectedEditBooking)}</span>
              </div>
              <div>
                <strong>Status</strong>
                <span>{selectedEditBooking.status || "Unknown"}</span>
              </div>
            </div>
          ) : (
            <div className="jp-admin-edit-bookings__empty">
              Select a booking to edit its accommodation.
            </div>
          )}
        </div>

        <div className="jp-admin-edit-card">
          <label className="jp-admin-field-label" htmlFor="jp-admin-edit-accommodation">
            Accommodation type
          </label>
          <select
            id="jp-admin-edit-accommodation"
            className="jp-admin-select"
            value={editAccommodationDraft.accommodation}
            onChange={(e) => handleEditAccommodationChange(e.target.value)}
            disabled={!selectedEditBooking || editSaving}
          >
            <option value="none">None / off-site</option>
            <option value="camp">Tent camping</option>
            <option value="camper">Campervan</option>
            <option value="room">Boarding room</option>
          </select>

          {editAccommodationDraft.accommodation === "camp" ? (
            <label className="jp-admin-edit-inline-field">
              <span>Tent pitches</span>
              <input
                type="number"
                min="1"
                className="jp-admin-input"
                value={editAccommodationDraft.campPitches}
                disabled={!selectedEditBooking || editSaving}
                onChange={(e) =>
                  updateEditAccommodationDraft({
                    campPitches: Math.max(1, Number.parseInt(e.target.value, 10) || 1),
                  })
                }
              />
            </label>
          ) : null}

          {editAccommodationDraft.accommodation === "camper" ? (
            <label className="jp-admin-edit-inline-field">
              <span>Campervan pitches</span>
              <input
                type="number"
                min="1"
                className="jp-admin-input"
                value={editAccommodationDraft.camperPitches}
                disabled={!selectedEditBooking || editSaving}
                onChange={(e) =>
                  updateEditAccommodationDraft({
                    camperPitches: Math.max(1, Number.parseInt(e.target.value, 10) || 1),
                  })
                }
              />
            </label>
          ) : null}

          {editAccommodationDraft.accommodation === "room" ? (
            <div className="jp-admin-edit-rooms">
              <div className="jp-admin-edit-rooms__top">
                <div>
                  <strong>Selected rooms</strong>
                  <span>
                    {selectedEditRooms.length} room{selectedEditRooms.length === 1 ? "" : "s"} /{" "}
                    {editSelectedRoomBeds} bed{editSelectedRoomBeds === 1 ? "" : "s"}
                  </span>
                </div>
                <button
                  type="button"
                  className="jp-admin-btn jp-admin-btn--secondary"
                  onClick={() => setEditRoomPickerOpen(true)}
                  disabled={!selectedEditBooking || editSaving}
                >
                  Choose rooms
                </button>
              </div>

              <div className="jp-admin-edit-rooms__availability">
                {unavailableRoomIdsForEdit.length} room{unavailableRoomIdsForEdit.length === 1 ? "" : "s"} unavailable from other paid or pending bookings.
              </div>

              {selectedEditRooms.length > 0 ? (
                <div className="jp-admin-edit-room-list">
                  {selectedEditRooms.map((room) => (
                    <span key={room.roomName}>
                      {room.roomName}
                      {room.capacity ? ` (${room.capacity} bed${room.capacity === 1 ? "" : "s"})` : ""}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="jp-admin-edit-bookings__empty">
                  No rooms selected yet. Use the room selector to see available rooms.
                </div>
              )}
            </div>
          ) : null}

          {editMessage ? (
            <div className="jp-admin-message">{editMessage}</div>
          ) : null}

          <div className="jp-admin-edit-bookings__actions">
            <button
              type="button"
              className="jp-admin-btn"
              onClick={handleSaveAccommodationEdit}
              disabled={!selectedEditBooking || editSaving}
            >
              {editSaving ? "Saving..." : "Save accommodation change"}
            </button>
          </div>
        </div>
      </div>

      {editRoomPickerOpen ? (
        <RoomPlanPicker
          selectedRoomIds={editAccommodationDraft.selectedRoomIds}
          unavailableRoomIds={unavailableRoomIdsForEdit}
          onChangeSelectedRoomIds={(selectedRoomIds) =>
            updateEditAccommodationDraft({ selectedRoomIds })
          }
          onRoomsByIdChange={setEditRoomsById}
          onClose={() => setEditRoomPickerOpen(false)}
        />
      ) : null}
    </section>
  );

  if (authLoading && !authChecked) {
    return <div className="jp-admin-panel">Checking access…</div>;
  }

  if (!isAuthed) {
    return (
      <div className="jp-admin-login-wrap">
        <div className="jp-admin-login-card">
          <h1 className="jp-admin-page__title">Admin access</h1>
          <p className="jp-admin-page__subtitle">
            Enter the admin password to view bookings.
          </p>

          <form onSubmit={handleLogin} className="jp-admin-login-form">
            <input
              type="password"
              className="jp-admin-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <button
              type="submit"
              className="jp-admin-btn"
              disabled={loginSubmitting}
            >
              {loginSubmitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          {authError ? (
            <div className="jp-admin-message">{authError}</div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="jp-admin-page">
      <div className="jp-admin-page__header">
        <div>
          <h1 className="jp-admin-page__title">
            {activeAdminTab === "checkin"
              ? "Event check-in"
              : activeAdminTab === "edit"
                ? "Edit bookings"
              : activeAdminTab === "email"
                ? "Email bookings"
              : activeAdminTab === "print"
                ? "Print reports"
                : "Bookings dashboard"}
          </h1>
          <p className="jp-admin-page__subtitle">
            {activeAdminTab === "checkin"
              ? "Find paid bookings, view details, and mark arrivals on the day."
              : activeAdminTab === "edit"
                ? "Make manual accommodation changes for bookings that need admin help."
              : activeAdminTab === "email"
                ? "Send rally updates to everyone, or to filtered groups of paid bookings."
              : activeAdminTab === "print"
                ? "Review meals, shirts, and boarding room lists, then print the selected table."
                : "Track paid bookings, accommodation mix, booking totals, and booking admin actions in one place."}
          </p>
        </div>

        <button
          type="button"
          className="jp-admin-btn jp-admin-btn--secondary"
          onClick={handleLogout}
        >
          Log out
        </button>
      </div>

      <nav className="jp-admin-tabs" aria-label="Admin portal sections">
        <button
          type="button"
          className={"jp-admin-tab" + (activeAdminTab === "dashboard" ? " active" : "")}
          onClick={() => setActiveAdminTab("dashboard")}
        >
          Dashboard
        </button>
        <button
          type="button"
          className={"jp-admin-tab" + (activeAdminTab === "checkin" ? " active" : "")}
          onClick={() => setActiveAdminTab("checkin")}
        >
          Event check-in
        </button>
        <button
          type="button"
          className={"jp-admin-tab" + (activeAdminTab === "edit" ? " active" : "")}
          onClick={() => setActiveAdminTab("edit")}
        >
          Edit bookings
        </button>
        <button
          type="button"
          className={"jp-admin-tab" + (activeAdminTab === "email" ? " active" : "")}
          onClick={() => setActiveAdminTab("email")}
        >
          Emails
        </button>
        <button
          type="button"
          className={"jp-admin-tab" + (activeAdminTab === "print" ? " active" : "")}
          onClick={() => setActiveAdminTab("print")}
        >
          Print reports
        </button>
      </nav>

      {activeAdminTab === "dashboard" ? (
      <>
      <section className="jp-admin-dashboard">
        <div className="jp-admin-stats-grid">
          <div className="jp-admin-stat-card">
            <div className="jp-admin-stat-label">Paid bookings</div>
            <div className="jp-admin-stat-value">
              {paidVisibleBookings.length}
            </div>
          </div>

          <div className="jp-admin-stat-card">
            <div className="jp-admin-stat-label">Paid income</div>
            <div className="jp-admin-stat-value">
              {formatCurrency(paidIncomeTotal)}
            </div>
          </div>

          <div className="jp-admin-stat-card">
            <div className="jp-admin-stat-label">Average booking value</div>
            <div className="jp-admin-stat-value">
              {formatCurrency(averagePaidBookingValue)}
            </div>
          </div>

          <div className="jp-admin-stat-card">
            <div className="jp-admin-stat-label">People on paid bookings</div>
            <div className="jp-admin-stat-value">{paidPeopleTotal}</div>
          </div>
        </div>

        <div className="jp-admin-charts-grid">
          <div className="jp-admin-chart-card">
            <div className="jp-admin-chart-title">Cumulative income</div>

            {cumulativeIncomeData.length === 0 ? (
              <div className="jp-admin-chart-empty">No paid bookings to chart.</div>
            ) : (
              <div className="jp-admin-chart-wrap">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={cumulativeIncomeData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(148, 163, 184, 0.22)"
                    />
                    <XAxis
                      dataKey="shortLabel"
                      tickLine={false}
                      axisLine={false}
                      minTickGap={24}
                      tick={{ fill: "#64748b", fontSize: 12 }}
                    />
                    <YAxis
                      tickFormatter={(value) => `£${Math.round(value)}`}
                      tickLine={false}
                      axisLine={false}
                      width={72}
                      tick={{ fill: "#64748b", fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value, name) =>
                        name === "cumulativeIncome"
                          ? [formatCurrency(value), "Cumulative income"]
                          : [formatCurrency(value), "Daily income"]
                      }
                      labelFormatter={(label, payload) =>
                        payload?.[0]?.payload?.fullDate || label
                      }
                      contentStyle={{
                        borderRadius: 16,
                        border: "1px solid rgba(15, 23, 42, 0.08)",
                        boxShadow: "0 16px 40px rgba(15, 23, 42, 0.14)",
                        background: "rgba(255,255,255,0.96)",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="cumulativeIncome"
                      stroke="#2563eb"
                      strokeWidth={3.5}
                      dot={false}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="jp-admin-chart-card">
            <div className="jp-admin-chart-title">
              Paid bookings by accommodation
            </div>

            {accommodationPieData.length === 0 ? (
              <div className="jp-admin-chart-empty">No paid bookings to chart.</div>
            ) : (
              <div className="jp-admin-chart-wrap">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={accommodationPieData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={95}
                      innerRadius={52}
                      paddingAngle={3}
                      cornerRadius={8}
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${Math.round((percent || 0) * 100)}%`
                      }
                    >
                      {accommodationPieData.map((entry, index) => (
                        <Cell
                          key={`cell-${entry.name}`}
                          fill={
                            ACCOMMODATION_COLOURS[
                              index % ACCOMMODATION_COLOURS.length
                            ]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [value, "Bookings"]}
                      contentStyle={{
                        borderRadius: 16,
                        border: "1px solid rgba(15, 23, 42, 0.08)",
                        boxShadow: "0 16px 40px rgba(15, 23, 42, 0.14)",
                        background: "rgba(255,255,255,0.96)",
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="jp-admin-toolbar">
        <input
          type="text"
          className="jp-admin-input"
          placeholder="Search by name, email, phone, room or merch…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="jp-admin-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          className="jp-admin-select"
          value={accommodationFilter}
          onChange={(e) => setAccommodationFilter(e.target.value)}
        >
          <option value="all">All accommodation</option>
          <option value="room">Room</option>
          <option value="camp">Camp</option>
          <option value="camper">Camper</option>
          <option value="none">None</option>
        </select>

        <button
          type="button"
          className="jp-admin-btn jp-admin-btn--secondary jp-admin-toolbar__button"
          onClick={handleDownloadPaidBookingSheets}
          disabled={
            paidPdfGenerating ||
            paidPricedPdfGenerating ||
            paidBookings.length === 0
          }
        >
          {paidPdfGenerating
            ? "Generating PDF…"
            : `Download paid PDF (${paidBookings.length})`}
        </button>

        <button
          type="button"
          className="jp-admin-btn jp-admin-btn--secondary jp-admin-toolbar__button"
          onClick={handleDownloadPaidPricedBookingSheets}
          disabled={
            paidPdfGenerating ||
            paidPricedPdfGenerating ||
            paidBookings.length === 0
          }
        >
          {paidPricedPdfGenerating
            ? "Generating priced PDF…"
            : `Download priced PDF (${paidBookings.length})`}
        </button>
      </section>

      <div className="jp-admin-results">
        {filteredBookings.length === bookings.length ? (
          <>
            {bookings.length} booking{bookings.length === 1 ? "" : "s"}
          </>
        ) : (
          <>
            Showing {filteredBookings.length} of {bookings.length} booking
            {bookings.length === 1 ? "" : "s"}
          </>
        )}
      </div>

      {resendMessage ? (
        <div className="jp-admin-message">{resendMessage}</div>
      ) : null}

      {loading ? (
        <div className="jp-admin-panel">Loading bookings…</div>
      ) : loadError ? (
        <div className="jp-admin-panel">Failed to load bookings: {loadError}</div>
      ) : (
        <div className="jp-admin-panel jp-admin-panel--table">
          <div className="jp-admin-table-wrap">
            <table className="jp-admin-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Booking ID</th>
                  <th>Booker</th>
                  <th>Contact</th>
                  <th>
                    People
                    <div className="jp-admin-th-sub">{visiblePeopleTotal} shown</div>
                  </th>
                  <th>Accommodation</th>
                  <th>Details</th>
                  <th>Meals</th>
                  <th>Merch</th>
                  <th>
                    Total
                    <div className="jp-admin-th-sub">
                      {formatCurrency(visibleCostTotal)}
                    </div>
                  </th>
                  <th>Booked</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="jp-admin-empty">
                      No bookings found.
                    </td>
                  </tr>
                ) : (
                  filteredBookings.map((booking) => {
                    const fullName = buildFullName(booking);

                    return (
                      <tr className="jp-admin-row" key={booking.id}>
                        <td>
                          <span className={getStatusClass(booking.status)}>
                            {booking.status || "Unknown"}
                          </span>
                        </td>

                        <td>
                          <div className="jp-admin-booking-id">
                            <code>{booking.id}</code>
                            <button
                              type="button"
                              className="jp-admin-copy-id-btn"
                              onClick={() => handleCopyBookingId(booking.id)}
                              aria-label={`Copy booking ID ${booking.id}`}
                              title="Copy booking ID"
                            >
                              Copy
                            </button>
                          </div>
                        </td>

                        <td>
                          <div className="jp-admin-booker">
                            <div className="jp-admin-booker__name">{fullName}</div>
                          </div>
                        </td>

                        <td>
                          <div>{booking.email || "—"}</div>
                          <div className="jp-admin-muted">{booking.phone || "—"}</div>
                        </td>

                        <td>
                          <div>{booking.total_people || 0} total</div>
                          <div className="jp-admin-muted">
                            {booking.adult_count || 0} adult
                            {Number(booking.adult_count || 0) === 1 ? "" : "s"}
                            {" • "}
                            {booking.child_count || 0} child
                            {Number(booking.child_count || 0) === 1 ? "" : "ren"}
                          </div>
                        </td>

                        <td>
                          <span className={getAccommodationClass(booking.accommodation)}>
                            {accommodationLabel(booking.accommodation)}
                          </span>
                        </td>

                        <td>{buildAccommodationSummary(booking)}</td>

                        <td>
                          <div>{buildMealsSummary(booking)}</div>
                          {booking.early_arrival ? (
                            <div className="jp-admin-muted">Early arrival</div>
                          ) : null}
                        </td>

                        <td>{buildMerchSummary(booking.merch)}</td>

                        <td>{formatCurrency(booking.total_cost)}</td>

                        <td>
                          <div>{formatDate(booking.created_at)}</div>
                          {String(booking.status || "").toLowerCase() === "pending" ? (
                            <div className="jp-admin-muted">
                              Expires: {formatDate(booking.expires_at)}
                            </div>
                          ) : null}
                        </td>

                        <td>
                          <div
                            className="jp-admin-actions-menu-wrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="jp-admin-icon-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveMenuBookingId((current) =>
                                  current === booking.id ? null : booking.id
                                );
                              }}
                              aria-label="Open actions menu"
                            >
                              ⋯
                            </button>

                            {activeMenuBookingId === booking.id ? (
                              <div className="jp-admin-actions-menu">
                                <button
                                  type="button"
                                  className="jp-admin-actions-menu__item"
                                  onClick={() => {
                                    openBookingDetail(booking);
                                    setActiveMenuBookingId(null);
                                  }}
                                >
                                  View booking
                                </button>

                                <button
                                  type="button"
                                  className="jp-admin-actions-menu__item"
                                  onClick={() => {
                                    handleResendEmail(booking);
                                    setActiveMenuBookingId(null);
                                  }}
                                  disabled={resendingId === booking.id}
                                >
                                  {resendingId === booking.id
                                    ? "Sending…"
                                    : "Resend email"}
                                </button>

                                <button
                                  type="button"
                                  className="jp-admin-actions-menu__item"
                                  onClick={() => {
                                    handleDownloadBookingSheet(booking);
                                    setActiveMenuBookingId(null);
                                  }}
                                  disabled={
                                    pdfBookingId === booking.id ||
                                    pricedPdfBookingId === booking.id
                                  }
                                >
                                  {pdfBookingId === booking.id
                                    ? "Generating…"
                                    : "Booking sheet"}
                                </button>

                                <button
                                  type="button"
                                  className="jp-admin-actions-menu__item"
                                  onClick={() => {
                                    handleDownloadPricedBookingSheet(booking);
                                    setActiveMenuBookingId(null);
                                  }}
                                  disabled={
                                    pdfBookingId === booking.id ||
                                    pricedPdfBookingId === booking.id
                                  }
                                >
                                  {pricedPdfBookingId === booking.id
                                    ? "Generating…"
                                    : "Priced booking sheet"}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>
      ) : activeAdminTab === "email" ? (
        renderGroupEmailSection()
      ) : activeAdminTab === "edit" ? (
        renderEditBookingsSection()
      ) : activeAdminTab === "checkin" ? (
        <section className="jp-admin-checkin" aria-label="Event check-in list">
          <div className="jp-admin-checkin__hero">
            <div>
              <div className="jp-admin-checkin__eyebrow">Jampot Rally 2026</div>
              <h2>Arrival desk</h2>
              <p>
                Search paid bookings, check the party details, and mark people as
                arrived for this session.
              </p>
            </div>

            <div className="jp-admin-checkin__summary">
              <div>
                <strong>{checkedInCount}</strong>
                <span>
                  checked in
                  <small>{checkedInPeopleTotal} people</small>
                </span>
              </div>
              <div>
                <strong>{earlyArrivalBookings.length}</strong>
                <span>
                  Thursday expected
                  <small>{earlyArrivalPeopleTotal} people</small>
                </span>
              </div>
              <div>
                <strong>{standardArrivalBookings.length}</strong>
                <span>
                  Rest expected
                  <small>{standardArrivalPeopleTotal} people</small>
                </span>
              </div>
            </div>
          </div>

          <div className="jp-admin-checkin__toolbar">
            <input
              type="text"
              className="jp-admin-input"
              placeholder="Search name, booking reference, email, phone or accommodation..."
              value={checkInSearch}
              onChange={(e) => setCheckInSearch(e.target.value)}
            />
            <div className="jp-admin-checkin__filters" aria-label="Check-in status filter">
              {[
                ["all", "All bookings"],
                ["checked-in", "Checked in"],
                ["awaiting", "Awaiting check-in"],
              ].map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  className={
                    "jp-admin-checkin__filter" +
                    (checkInStatusFilter === value ? " is-active" : "")
                  }
                  onClick={() => setCheckInStatusFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="jp-admin-checkin__shown">
              Showing {checkInBookings.length} of {paidBookings.length}
            </div>
          </div>

          {loading ? (
            <div className="jp-admin-panel">Loading bookings...</div>
          ) : loadError ? (
            <div className="jp-admin-panel">Failed to load bookings: {loadError}</div>
          ) : checkInBookings.length === 0 ? (
            <div className="jp-admin-checkin__empty">No paid bookings match that search.</div>
          ) : (
            <div className="jp-admin-checkin__list">
              {checkInBookings.map((booking) => {
                const isCheckedIn = Boolean(checkedInBookings[booking.id]);
                const people = Number(booking.total_people || 0);
                const companionNames = bookingCompanionNames(booking);

                return (
                  <article
                    className={
                      "jp-admin-checkin-card" + (isCheckedIn ? " is-checked-in" : "")
                    }
                    key={booking.id}
                  >
                    <div className="jp-admin-checkin-card__main">
                      <div className="jp-admin-checkin-card__status">
                        {isCheckedIn ? "Checked in" : "Awaiting arrival"}
                      </div>
                      <h3>{buildFullName(booking)}</h3>
                      {companionNames.length > 0 ? (
                        <div className="jp-admin-checkin-card__companions">
                          {companionNames.join(", ")}
                        </div>
                      ) : null}
                      <p>
                        {people} people
                        {" | "}
                        {accommodationLabel(booking.accommodation)}
                        {" | "}
                        {buildMealsSummary(booking)}
                      </p>
                      <div className="jp-admin-checkin-card__meta">
                        <span>{booking.id}</span>
                        <span>{booking.email || "No email"}</span>
                        <span>{booking.phone || "No phone"}</span>
                      </div>
                    </div>

                    <div className="jp-admin-checkin-card__actions">
                      <button
                        type="button"
                        className={
                          "jp-admin-checkin-card__check" +
                          (isCheckedIn ? " is-checked-in" : "")
                        }
                        onClick={() => handleToggleCheckIn(booking.id)}
                        aria-pressed={isCheckedIn}
                      >
                        {isCheckedIn ? "Checked in" : "Check in"}
                      </button>
                      <button
                        type="button"
                        className="jp-admin-btn jp-admin-btn--secondary"
                        onClick={() => openBookingDetail(booking, "checkin")}
                      >
                        View details
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      ) : (
        <section className="jp-admin-print" aria-label="Printable booking reports">
          <div className="jp-admin-print__intro">
            <div>
              <div className="jp-admin-kicker">Print Reports</div>
              <h2>Event paperwork</h2>
              <p>
                Choose the report you need, check the table, then print the selected
                list for use on the day.
              </p>
            </div>

            <button
              type="button"
              className="jp-admin-btn jp-admin-print__button"
              onClick={handlePrintReport}
              disabled={loading || Boolean(loadError)}
            >
              Print table
            </button>
          </div>

          <div className="jp-admin-print__reports" role="tablist" aria-label="Printable reports">
            {PRINT_REPORTS.map((report) => (
              <button
                key={report.key}
                type="button"
                className={
                  "jp-admin-print__report" +
                  (printReport === report.key ? " active" : "")
                }
                onClick={() => setPrintReport(report.key)}
              >
                <span>{report.label}</span>
                <strong>{reportCounts[report.key] || 0}</strong>
                <small>{report.description}</small>
              </button>
            ))}
          </div>

          <div className="jp-admin-print__sheet" id="jp-admin-print-sheet">
            <div className="jp-admin-print__sheet-header">
              <div>
                <h2>{selectedPrintReport.label}</h2>
                <p>Jampot Rally 2026 Devon &amp; Cornwall</p>
              </div>

              {printReport === "meals" ? (
                <div className="jp-admin-print__totals">
                  <strong>{mealReportTotals.sundayBbqCovers}</strong>
                  <span>Sunday BBQ total</span>
                  <small>
                    {mealReportTotals.mealCovers} meals • {mealReportTotals.bbqPasses} extra BBQ •{" "}
                    {mealReportTotals.coeliacCovers} coeliac
                  </small>
                </div>
              ) : printReport === "shirts" ? (
                <div className="jp-admin-print__totals">
                  <strong>{shirtReportTotals.total}</strong>
                  <span>shirts</span>
                  <small>{buildShirtSizeSummary({ shirts: shirtReportTotals.sizes })}</small>
                </div>
              ) : printReport === "rooms" ? (
                <div className="jp-admin-print__totals">
                  <strong>{roomReportRows.length}</strong>
                  <span>rooms</span>
                  <small>{paidBookings.length} paid bookings</small>
                </div>
              ) : printReport === "checkin-form" ? (
                <div className="jp-admin-print__totals">
                  <strong>{checkInFormRows.length}</strong>
                  <span>lead bookers</span>
                  <small>Record bike make/model, registration and ridden mileage</small>
                </div>
              ) : (
                <div className="jp-admin-print__totals">
                  <strong>{attendeeListRows.length}</strong>
                  <span>attendees</span>
                  <small>Names, mobiles and accommodation inherited from lead bookings</small>
                </div>
              )}
            </div>

            {loading ? (
              <div className="jp-admin-panel">Loading bookings...</div>
            ) : loadError ? (
              <div className="jp-admin-panel">Failed to load bookings: {loadError}</div>
            ) : printReport === "meals" ? (
              <div className="jp-admin-table-wrap jp-admin-print__table-wrap">
                <table className="jp-admin-table jp-admin-print-table">
                  <thead>
                    <tr>
                      <th>Booking</th>
                      <th>Lead booker</th>
                      <th>People</th>
                      <th>Meals</th>
                      <th>Extra BBQ</th>
                      <th>Sunday BBQ total</th>
                      <th>Coeliac</th>
                      <th>Contact</th>
                      <th>Arrival</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mealReportRows.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="jp-admin-empty">
                          No paid bookings have meal packages or extra BBQ passes.
                        </td>
                      </tr>
                    ) : (
                      mealReportRows.map(({ booking, mealCovers, bbqPasses, coeliacCovers }) => (
                        <tr key={`meal-${booking.id}`}>
                          <td>{booking.id}</td>
                          <td>{buildFullName(booking)}</td>
                          <td>{booking.total_people || 0}</td>
                          <td>{mealCovers}</td>
                          <td>{bbqPasses}</td>
                          <td>{mealCovers + bbqPasses}</td>
                          <td>{coeliacCovers}</td>
                          <td>
                            <div>{booking.email || "—"}</div>
                            <div className="jp-admin-muted">{booking.phone || "—"}</div>
                          </td>
                          <td>{booking.early_arrival ? "Thursday" : "Friday"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : printReport === "shirts" ? (
              <div className="jp-admin-table-wrap jp-admin-print__table-wrap">
                <table className="jp-admin-table jp-admin-print-table">
                  <thead>
                    <tr>
                      <th>Booking</th>
                      <th>Lead booker</th>
                      <th>Total</th>
                      {SHIRT_SIZES.map((size) => (
                        <th key={`shirt-head-${size}`}>{size}</th>
                      ))}
                      <th>Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shirtReportRows.length === 0 ? (
                      <tr>
                        <td colSpan={SHIRT_SIZES.length + 4} className="jp-admin-empty">
                          No paid bookings have shirts ordered.
                        </td>
                      </tr>
                    ) : (
                      shirtReportRows.map(({ booking, shirts, totalShirts }) => (
                        <tr key={`shirt-${booking.id}`}>
                          <td>{booking.id}</td>
                          <td>{buildFullName(booking)}</td>
                          <td>{totalShirts}</td>
                          {SHIRT_SIZES.map((size) => (
                            <td key={`${booking.id}-shirt-${size}`}>
                              {Number(shirts?.[size] || 0)}
                            </td>
                          ))}
                          <td>
                            <div>{booking.email || "—"}</div>
                            <div className="jp-admin-muted">{booking.phone || "—"}</div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : printReport === "rooms" ? (
              <div className="jp-admin-table-wrap jp-admin-print__table-wrap">
                <table className="jp-admin-table jp-admin-print-table">
                  <thead>
                    <tr>
                      <th>Room</th>
                      <th>Room size</th>
                      <th>Booking</th>
                      <th>Lead booker</th>
                      <th>People</th>
                      <th>Names</th>
                      <th>Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roomReportRows.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="jp-admin-empty">
                          No paid bookings have boarding rooms.
                        </td>
                      </tr>
                    ) : (
                      roomReportRows.map(({ booking, room, index }) => (
                        <tr key={`room-${booking.id}-${index}`}>
                          <td>{room?.roomName || "Unknown room"}</td>
                          <td>{roomCapacityLabel(room)}</td>
                          <td>{booking.id}</td>
                          <td>{buildFullName(booking)}</td>
                          <td>{booking.total_people || 0}</td>
                          <td>{bookingPartyNames(booking)}</td>
                          <td>
                            <div>{booking.email || "—"}</div>
                            <div className="jp-admin-muted">{booking.phone || "—"}</div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : printReport === "attendee-list" ? (
              <div className="jp-admin-table-wrap jp-admin-print__table-wrap">
                <table className="jp-admin-table jp-admin-print-table jp-admin-attendee-list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Staying</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendeeListRows.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="jp-admin-empty">
                          No named attendees to include.
                        </td>
                      </tr>
                    ) : (
                      attendeeListRows.map((row, index) => (
                        <tr key={`attendee-list-${row.bookingId}-${index}`}>
                          <td>{row.name}</td>
                          <td>{row.phone}</td>
                          <td>{row.accommodation}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="jp-admin-table-wrap jp-admin-print__table-wrap">
                <p className="jp-admin-checkin-form-note">
                  Record bike make/model, registration, whether the bike was ridden from home,
                  and mileage ridden to the rally. Mileage should not include trailer or van transport.
                </p>
                <table className="jp-admin-table jp-admin-print-table jp-admin-checkin-form-table">
                  <thead>
                    <tr>
                      <th>Lead booker</th>
                      <th>Mobile</th>
                      <th>People</th>
                      <th>Accommodation</th>
                      <th>Room</th>
                      <th>Meals</th>
                      <th>Extra badges</th>
                      <th>Shirts</th>
                      <th>Bike make / model</th>
                      <th>Registration</th>
                      <th>Ridden from home?</th>
                      <th>Ridden mileage</th>
                      <th>Award notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkInFormRows.length === 0 ? (
                      <tr>
                        <td colSpan="13" className="jp-admin-empty">
                          No paid bookings to include on the check-in form.
                        </td>
                      </tr>
                    ) : (
                      checkInFormRows.map((booking) => (
                        <tr key={`checkin-form-${booking.id}`}>
                          <td>
                            <strong>{buildFullName(booking)}</strong>
                          </td>
                          <td>{booking.phone || "—"}</td>
                          <td>{booking.total_people || 0}</td>
                          <td>{buildAccommodationTypeSummary(booking)}</td>
                          <td>{buildRoomNameSummary(booking)}</td>
                          <td>{buildMealsSummary(booking)}</td>
                          <td>{countExtraBadges(booking)}</td>
                          <td>{buildShirtSizeSummary(booking.merch)}</td>
                          <td className="jp-admin-form-cell" aria-label="Bike make and model"></td>
                          <td className="jp-admin-form-cell" aria-label="Bike registration"></td>
                          <td className="jp-admin-form-cell jp-admin-form-cell--short" aria-label="Ridden from home"></td>
                          <td className="jp-admin-form-cell jp-admin-form-cell--short" aria-label="Ridden mileage"></td>
                          <td className="jp-admin-form-cell" aria-label="Award notes"></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {viewingBooking ? (
        <div
          className="jp-admin-modal-backdrop"
          onClick={closeBookingDetail}
        >
          <div
            className="jp-admin-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="jp-admin-modal__header">
              <div>
                <h2 className="jp-admin-modal__title">
                  {buildFullName(viewingBooking)}
                </h2>
                <p className="jp-admin-modal__subtitle">
                  {bookingDetailMode === "checkin"
                    ? "Read-only check-in details"
                    : "Booking details and attendee information"}
                </p>
              </div>

              <button
                type="button"
                className="jp-admin-icon-btn"
                onClick={closeBookingDetail}
                aria-label="Close booking details"
              >
                ×
              </button>
            </div>

            <div className="jp-admin-detail-grid">
              <div className="jp-admin-detail-card">
                <h3>Lead booker</h3>
                <p>
                  <strong>Name:</strong> {buildFullName(viewingBooking)}
                </p>
                <p>
                  <strong>Email:</strong> {viewingBooking.email || "—"}
                </p>
                <p>
                  <strong>Phone:</strong> {viewingBooking.phone || "—"}
                </p>
                <p>
                  <strong>Status:</strong> {viewingBooking.status || "—"}
                </p>
                <p>
                  <strong>Total cost:</strong>{" "}
                  {formatCurrency(viewingBooking.total_cost)}
                </p>
                <p>
                  <strong>Booked:</strong> {formatDate(viewingBooking.created_at)}
                </p>
              </div>

              <div className="jp-admin-detail-card">
                <h3>Accommodation</h3>
                <p>
                  <strong>Type:</strong>{" "}
                  {accommodationLabel(viewingBooking.accommodation)}
                </p>
                <p>
                  <strong>Summary:</strong>{" "}
                  {buildAccommodationSummary(viewingBooking)}
                </p>

                {Array.isArray(viewingBooking.rooms_booked) &&
                viewingBooking.rooms_booked.length > 0 ? (
                  <>
                    <p>
                      <strong>Rooms:</strong>
                    </p>
                    <ul className="jp-admin-list">
                      {viewingBooking.rooms_booked.map((room, index) => (
                        <li key={`${viewingBooking.id}-room-${index}`}>
                          {room?.roomName || "Unknown room"}
                          {room?.capacity ? ` (${room.capacity} bed)` : ""}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>

              <div className="jp-admin-detail-card">
                <h3>Attendees</h3>
                <p>
                  <strong>Adults:</strong> {viewingBooking.adult_count || 0}
                </p>
                <p>
                  <strong>Children:</strong> {viewingBooking.child_count || 0}
                </p>

                <p>
                  <strong>Adult names:</strong>
                </p>
                <ul className="jp-admin-list">
                  {attendeeNames(viewingBooking.attendees, "adult").length ? (
                    attendeeNames(viewingBooking.attendees, "adult").map(
                      (name, index) => (
                        <li key={`${viewingBooking.id}-adult-${index}`}>
                          {name}
                        </li>
                      )
                    )
                  ) : (
                    <li>—</li>
                  )}
                </ul>

                <p>
                  <strong>Child names:</strong>
                </p>
                <ul className="jp-admin-list">
                  {attendeeNames(viewingBooking.attendees, "child").length ? (
                    attendeeNames(viewingBooking.attendees, "child").map(
                      (name, index) => (
                        <li key={`${viewingBooking.id}-child-${index}`}>
                          {name}
                        </li>
                      )
                    )
                  ) : (
                    <li>—</li>
                  )}
                </ul>
              </div>

              <div className="jp-admin-detail-card">
                <h3>Food & merch</h3>
                <p>
                  <strong>Meals:</strong>{" "}
                  {buildMealsSummary(viewingBooking)}
                </p>
                {bookingDetailMode !== "checkin" ? (
                <div style={{ display: "grid", gap: "0.6rem", margin: "0.75rem 0 0.9rem" }}>
                  <label className="jp-admin-field-label" htmlFor="jp-admin-coeliac-meals">
                    Coeliac meals
                  </label>
                  <input
                    id="jp-admin-coeliac-meals"
                    type="number"
                    className="jp-admin-input"
                    min={0}
                    max={getMealCovers(viewingBooking)}
                    value={coeliacMealDraft}
                    onChange={(e) => {
                      const value = Number.parseInt(e.target.value, 10);
                      const next = Number.isNaN(value)
                        ? 0
                        : Math.max(0, Math.min(getMealCovers(viewingBooking), value));
                      setCoeliacMealDraft(next);
                      setCoeliacMealMessage("");
                    }}
                    disabled={getMealCovers(viewingBooking) <= 0 || coeliacMealSaving}
                  />
                  <div className="jp-admin-muted">
                    Maximum {getMealCovers(viewingBooking)} based on this booking’s meal package count.
                  </div>
                  <button
                    type="button"
                    className="jp-admin-btn"
                    onClick={handleSaveCoeliacMeals}
                    disabled={
                      coeliacMealSaving ||
                      getMealCovers(viewingBooking) <= 0 ||
                      getCoeliacMealCovers(viewingBooking) === Number(coeliacMealDraft || 0)
                    }
                  >
                    {coeliacMealSaving ? "Saving…" : "Save coeliac meals"}
                  </button>
                  {coeliacMealMessage ? (
                    <div className="jp-admin-muted">{coeliacMealMessage}</div>
                  ) : null}
                </div>
                ) : (
                  <p>
                    <strong>Coeliac meals:</strong>{" "}
                    {getCoeliacMealCovers(viewingBooking)}
                  </p>
                )}
                <p>
                  <strong>Early arrival:</strong>{" "}
                  {viewingBooking.early_arrival ? "Yes" : "No"}
                </p>
                <p>
                  <strong>Merch:</strong>{" "}
                  {buildMerchSummary(viewingBooking.merch)}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
